import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Package,
  Save,
  X,
  Settings2,
  Loader2,
  Layers,
  ScanBarcode,
  Calendar,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import DefaultEditor from "react-simple-wysiwyg";
import Modal from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn, formatDate } from "@/lib/utils";
import InventorySelectorModal from "../../sales/components/pos/InventorySelectorModal";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";

const AddPartsModal = ({
  show,
  onHide,
  selectedRepairJob,
  partsFormData,
  setPartsFormData,
  handleProductSelect,
  isProductsLoading,
  products,
  currencySymbol,
  handleSerialNumbersChange,
  handleSaveParts,
  addPartsMutation,
  handleViewParts,
  repairJobs,
}) => {
  const [showInventorySelector, setShowInventorySelector] = useState(false);
  const [selectorType, setSelectorType] = useState("standard");
  const [availableBatches, setAvailableBatches] = useState([]);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [, setIsLoadingInventory] = useState(false);

  // Find selected product
  const selectedProduct = useMemo(() => {
    if (!partsFormData?.product_id) return null;
    return products.find(
      (p) => String(p.id) === String(partsFormData.product_id),
    );
  }, [partsFormData?.product_id, products]);

  // Determine tracking type: "serial", "standard" (batch), or "none"
  const trackingType = useMemo(() => {
    if (partsFormData?.tracking_type) return partsFormData.tracking_type;
    if (!selectedProduct) return "none";
    if (
      selectedProduct.tracking_type === "serial" ||
      selectedProduct.is_serialized
    ) {
      return "serial";
    }
    if (
      selectedProduct.tracking_type === "standard" ||
      selectedProduct.has_batches
    ) {
      return "standard";
    }
    return selectedProduct.tracking_type || "none";
  }, [partsFormData?.tracking_type, selectedProduct]);

  // Fetch batches & serial numbers whenever selected product changes
  useEffect(() => {
    const productId = partsFormData?.product_id;
    if (!productId) {
      setAvailableBatches([]);
      setAvailableSerials([]);
      return;
    }

    const prod = products.find((p) => String(p.id) === String(productId));
    if (Array.isArray(prod?.batches)) {
      setAvailableBatches(prod.batches);
    }
    if (Array.isArray(prod?.serial_numbers)) {
      setAvailableSerials(prod.serial_numbers);
    }

    let isMounted = true;
    const fetchInventoryDetails = async () => {
      setIsLoadingInventory(true);
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(productId),
        );
        const details = res.data?.data;
        if (isMounted && details) {
          if (Array.isArray(details.batches)) {
            setAvailableBatches(details.batches);
          }
          if (Array.isArray(details.serial_numbers)) {
            setAvailableSerials(details.serial_numbers);
          }
          if (details.tracking_type || details.is_serialized || details.has_batches) {
            const detectedType =
              details.tracking_type ||
              (details.is_serialized
                ? "serial"
                : details.has_batches
                  ? "standard"
                  : "none");
            setPartsFormData((prev) => ({
              ...prev,
              tracking_type: detectedType,
            }));
          }
        }
      } catch {
        // Fallback to existing product details if fetch fails
      } finally {
        if (isMounted) setIsLoadingInventory(false);
      }
    };

    fetchInventoryDetails();

    return () => {
      isMounted = false;
    };
  }, [partsFormData?.product_id, products, setPartsFormData]);

  // Filter out serial numbers already used on this repair job
  const filteredSerials = useMemo(() => {
    const usedSerialsInJob = (selectedRepairJob?.parts || []).reduce(
      (acc, p) => {
        if (
          String(p.product_id) === String(partsFormData?.product_id) &&
          Array.isArray(p.serial_numbers)
        ) {
          p.serial_numbers.forEach((sn) => {
            acc.push(typeof sn === "string" ? sn : sn.serial_number);
          });
        }
        return acc;
      },
      [],
    );

    return availableSerials.filter((s) => {
      const snValue = typeof s === "string" ? s : s.serial_number;
      return !usedSerialsInJob.includes(snValue);
    });
  }, [availableSerials, partsFormData?.product_id, selectedRepairJob?.parts]);

  // Currently selected batch object
  const selectedBatch = useMemo(() => {
    if (!partsFormData?.inventory_batch_id) return null;
    return availableBatches.find(
      (b) => String(b.id) === String(partsFormData.inventory_batch_id),
    );
  }, [availableBatches, partsFormData?.inventory_batch_id]);

  const requiredQty = parseFloat(partsFormData?.quantity_used) || 1;
  const selectedSerialsCount = (partsFormData?.serial_numbers || []).length;
  const isSerialCountValid = selectedSerialsCount === requiredQty;
  const isBatchSelected = Boolean(partsFormData?.inventory_batch_id);

  // Validate before invoking handleSaveParts
  const handleValidateAndSave = () => {
    if (!partsFormData.product_id) {
      toast.error("Please select a product");
      return;
    }
    const qty = parseFloat(partsFormData.quantity_used);
    if (!qty || qty <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }

    if (trackingType === "standard" && !isBatchSelected) {
      toast.error("Please select an inventory batch for this product");
      return;
    }

    if (trackingType === "serial" && !isSerialCountValid) {
      toast.error(
        `Please select exactly ${requiredQty} serial number${requiredQty > 1 ? "s" : ""} (currently selected: ${selectedSerialsCount})`,
      );
      return;
    }

    handleSaveParts();
  };

  const handleOpenSelector = (type) => {
    setSelectorType(type);
    setShowInventorySelector(true);
  };

  const handleInventorySelect = (selection) => {
    if (selectorType === "standard") {
      const batch = availableBatches.find(
        (b) => String(b.id) === String(selection),
      );
      setPartsFormData((prev) => ({
        ...prev,
        inventory_batch_id: selection,
        inventory_batch_number: batch?.batch_number || "",
      }));
      setShowInventorySelector(false);
    } else if (selectorType === "serial") {
      const serialArr = Array.isArray(selection) ? selection : [selection];
      setPartsFormData((prev) => ({
        ...prev,
        serial_numbers: serialArr,
      }));
      setShowInventorySelector(false);
    }
  };

  const isSaveDisabled =
    Boolean(addPartsMutation?.isPending) ||
    !partsFormData?.product_id ||
    !partsFormData?.quantity_used ||
    parseFloat(partsFormData.quantity_used) <= 0 ||
    (trackingType === "standard" && !isBatchSelected) ||
    (trackingType === "serial" && !isSerialCountValid);

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(v) => !v && onHide()}
        title={
          <div className="flex items-center gap-2">
            <span>Add Parts</span>
            <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {selectedRepairJob?.item_description}
            </span>
          </div>
        }
        icon={Package}
        maxWidth="max-w-xl"
        footer={
          <div className="flex justify-between w-full">
            <Button
              variant="ghost"
              className="gap-2 text-primary hover:text-primary hover:bg-primary/5"
              onClick={() => {
                onHide();
                handleViewParts(
                  repairJobs.find((job) => job.id === selectedRepairJob.id),
                );
              }}
            >
              <Settings2 size={16} /> View All Parts
            </Button>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onHide}
                disabled={addPartsMutation?.isPending}
              >
                Cancel
              </Button>
              <Button
                className="gap-2"
                onClick={handleValidateAndSave}
                disabled={isSaveDisabled}
              >
                {addPartsMutation?.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 px-1">
          {/* Product Select */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="font-medium">
                Product <span className="text-destructive">*</span>
              </Label>
              {selectedProduct && (
                <div className="flex items-center gap-1.5">
                  {trackingType === "standard" && (
                    <Badge variant="outline" className="gap-1 text-[11px] text-amber-600 border-amber-300 bg-amber-50">
                      <Layers size={11} /> Batch Tracked
                    </Badge>
                  )}
                  {trackingType === "serial" && (
                    <Badge variant="outline" className="gap-1 text-[11px] text-blue-600 border-blue-300 bg-blue-50">
                      <ScanBarcode size={11} /> Serial Tracked
                    </Badge>
                  )}
                  {trackingType === "none" && (
                    <Badge variant="outline" className="text-[11px] text-muted-foreground">
                      No Tracking
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <ReactSelect
              options={products.map((product) => ({
                value: product.id,
                label: `${product.name} - ${currencySymbol}${parseFloat(
                  product.sell_price || 0,
                ).toFixed(2)}${product.sku ? ` [${product.sku}]` : ""}`,
              }))}
              placeholder={
                isProductsLoading ? "Loading products..." : "Select Product"
              }
              value={
                partsFormData.product_id
                  ? {
                    value: partsFormData.product_id,
                    label: (() => {
                      const p = products.find(
                        (prod) => prod.id === partsFormData.product_id,
                      );
                      return p
                        ? `${p.name} - ${currencySymbol}${parseFloat(
                          p.sell_price || 0,
                        ).toFixed(2)}${p.sku ? ` [${p.sku}]` : ""}`
                        : "Select Product";
                    })(),
                  }
                  : null
              }
              onChange={(option) => {
                const newProductId = option ? option.value : "";
                const newProd = products.find((p) => p.id === newProductId);
                const detectedType =
                  newProd?.tracking_type ||
                  (newProd?.is_serialized
                    ? "serial"
                    : newProd?.has_batches
                      ? "standard"
                      : "none");

                setPartsFormData((prev) => ({
                  ...prev,
                  product_id: newProductId,
                  unit_cost: parseFloat(newProd?.sell_price || 0),
                  tracking_type: detectedType,
                  inventory_batch_id: null,
                  inventory_batch_number: "",
                  serial_numbers: [],
                }));

                handleProductSelect({
                  target: { value: newProductId },
                });
              }}
              isLoading={isProductsLoading}
              isDisabled={isProductsLoading}
            />
          </div>

          {/* Quantity & Unit Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-medium">
                Quantity Used <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="1"
                value={partsFormData.quantity_used}
                onChange={(e) => {
                  const newQty = e.target.value;
                  setPartsFormData((prev) => ({
                    ...prev,
                    quantity_used: newQty,
                  }));
                }}
                min="0.01"
                max="999999.99"
                step="0.01"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium">
                Unit Sell Price <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  {currencySymbol}
                </span>
                <Input
                  type="number"
                  className="pl-7"
                  placeholder="0.00"
                  value={partsFormData.unit_cost}
                  onChange={(e) =>
                    setPartsFormData({
                      ...partsFormData,
                      unit_cost: e.target.value,
                    })
                  }
                  min="0"
                  max="999999.99"
                  step="0.01"
                  required
                />
              </div>
            </div>
          </div>

          {/* Batch Tracking Section */}
          {trackingType === "standard" && (
            <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-1.5">
                  <Layers size={14} className="text-amber-600" />
                  Inventory Batch <span className="text-destructive">*</span>
                </Label>
                {selectedBatch && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      Stock: {selectedBatch.quantity ?? selectedBatch.current_stock ?? "N/A"}
                    </Badge>
                    {selectedBatch.expiry_date && (
                      <span className="flex items-center gap-1 text-[10px]">
                        <Calendar size={10} /> {formatDate(selectedBatch.expiry_date)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-between h-10 text-xs",
                  !isBatchSelected
                    ? "border-destructive text-destructive hover:bg-destructive/5"
                    : "border-emerald-500 text-emerald-700 bg-emerald-50/40 hover:bg-emerald-50/60",
                )}
                onClick={() => handleOpenSelector("standard")}
              >
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  <span className="font-medium text-sm">
                    {selectedBatch?.batch_number ||
                      partsFormData.inventory_batch_number ||
                      (isBatchSelected ? "Batch Selected" : "Select Batch *")}
                  </span>
                </div>
                <Badge
                  variant={isBatchSelected ? "default" : "destructive"}
                  className="text-[10px] h-5 px-2"
                >
                  {isBatchSelected ? "Batch Selected" : "Batch Required"}
                </Badge>
              </Button>

              {!isBatchSelected && (
                <p className="text-[11px] text-destructive flex items-center gap-1 mt-1 font-medium">
                  <AlertCircle size={12} /> Please select an inventory batch for this item.
                </p>
              )}
            </div>
          )}

          {/* Serial Tracking Section */}
          {trackingType === "serial" && (
            <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-1.5">
                  <ScanBarcode size={14} className="text-blue-600" />
                  Serial Numbers <span className="text-destructive">*</span>
                </Label>
                <span
                  className={cn(
                    "text-xs font-semibold flex items-center gap-1",
                    isSerialCountValid ? "text-emerald-600" : "text-destructive",
                  )}
                >
                  {isSerialCountValid ? (
                    <CheckCircle2 size={12} />
                  ) : (
                    <AlertCircle size={12} />
                  )}
                  {selectedSerialsCount} / {requiredQty} Selected
                </span>
              </div>

              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-between h-10 text-xs",
                  !isSerialCountValid
                    ? "border-destructive text-destructive hover:bg-destructive/5"
                    : "border-emerald-500 text-emerald-700 bg-emerald-50/40 hover:bg-emerald-50/60",
                )}
                onClick={() => handleOpenSelector("serial")}
              >
                <div className="flex items-center gap-2">
                  <ScanBarcode className="h-4 w-4" />
                  <span className="font-medium text-sm">
                    {isSerialCountValid
                      ? `${selectedSerialsCount} Serial Numbers Selected`
                      : `Select ${requiredQty} Serial Number${requiredQty > 1 ? "s" : ""} *`}
                  </span>
                </div>
                <Badge
                  variant={isSerialCountValid ? "default" : "destructive"}
                  className="text-[10px] h-5 px-2"
                >
                  {selectedSerialsCount}/{requiredQty}
                </Badge>
              </Button>

              {/* Selected Serials Badges */}
              {partsFormData.serial_numbers?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {partsFormData.serial_numbers.map((sn, idx) => (
                    <Badge
                      key={idx}
                      variant="secondary"
                      className="gap-1.5 text-xs py-0.5 px-2 font-mono bg-background border"
                    >
                      <ScanBarcode size={11} className="text-muted-foreground" />
                      <span>{typeof sn === "string" ? sn : sn.serial_number}</span>
                      <button
                        type="button"
                        aria-label="Remove serial"
                        className="text-muted-foreground hover:text-destructive ml-1"
                        onClick={() => {
                          const updated = partsFormData.serial_numbers.filter(
                            (_, i) => i !== idx,
                          );
                          setPartsFormData((prev) => ({
                            ...prev,
                            serial_numbers: updated,
                          }));
                          if (handleSerialNumbersChange) {
                            handleSerialNumbersChange(updated.join(", "));
                          }
                        }}
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {!isSerialCountValid && (
                <p className="text-[11px] text-destructive flex items-center gap-1 font-medium">
                  <AlertCircle size={12} />
                  Please select {requiredQty} serial number{requiredQty > 1 ? "s" : ""} to match the quantity.
                </p>
              )}
            </div>
          )}

          {/* No Tracking Notice */}
          {trackingType === "none" && partsFormData.product_id && (
            <div className="p-2.5 rounded-md bg-muted/40 border border-muted text-xs text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span>Standard Product: No serial numbers or batch tracking required.</span>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="font-medium">Notes</Label>
            <div className="rounded-md border border-input overflow-hidden min-h-[140px]">
              <DefaultEditor
                value={partsFormData.notes || ""}
                onChange={(e) =>
                  setPartsFormData({
                    ...partsFormData,
                    notes: e.target.value,
                  })
                }
                style={{
                  height: "140px",
                  border: "none",
                  fontSize: "14px",
                }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground text-right italic">
              {(partsFormData.notes || "").length}/1000 characters
            </p>
          </div>
        </div>
      </Modal>

      {/* Inventory Selector Modal for Batch / Serials */}
      <InventorySelectorModal
        show={showInventorySelector}
        onHide={() => {
          setShowInventorySelector(false);
        }}
        type={selectorType}
        availableItems={
          selectorType === "serial" ? filteredSerials : availableBatches
        }
        selectedItems={
          selectorType === "serial"
            ? (partsFormData.serial_numbers || []).map((s) =>
              typeof s === "string" ? s : s.serial_number,
            )
            : partsFormData.inventory_batch_id
              ? [partsFormData.inventory_batch_id]
              : []
        }
        onSelect={handleInventorySelect}
        quantity={requiredQty}
      />
    </>
  );
};

AddPartsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedRepairJob: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    item_description: PropTypes.string,
    parts: PropTypes.array,
  }),
  partsFormData: PropTypes.shape({
    product_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    quantity_used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unit_cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    serial_numbers: PropTypes.array,
    inventory_batch_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    inventory_batch_number: PropTypes.string,
    tracking_type: PropTypes.string,
    notes: PropTypes.string,
  }).isRequired,
  setPartsFormData: PropTypes.func.isRequired,
  handleProductSelect: PropTypes.func.isRequired,
  isProductsLoading: PropTypes.bool,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      sell_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sku: PropTypes.string,
      tracking_type: PropTypes.string,
      is_serialized: PropTypes.bool,
      has_batches: PropTypes.bool,
      batches: PropTypes.array,
      serial_numbers: PropTypes.array,
    }),
  ).isRequired,
  currencySymbol: PropTypes.string.isRequired,
  handleSerialNumbersChange: PropTypes.func,
  handleSaveParts: PropTypes.func.isRequired,
  addPartsMutation: PropTypes.shape({
    isPending: PropTypes.bool,
  }),
  handleViewParts: PropTypes.func.isRequired,
  repairJobs: PropTypes.array.isRequired,
};

export default AddPartsModal;
